import { forwardRef, HTMLAttributes } from 'react';
import { cn } from './cn';
import { calculateAge, getDisplayName, getInitials } from '../utils';

export interface ProfileHeaderProfile {
  given_name?: string | null;
  family_name?: string | null;
  email?: string;
  profile_picture_url?: string | null;
  position?: string | null;
  jersey_number?: string | null;
  height?: string | null;
  weight?: string | null;
  date_of_birth?: string | null;
}

export interface ProfileHeaderProps extends HTMLAttributes<HTMLDivElement> {
  profile: ProfileHeaderProfile;
}

interface StatProps {
  label: string;
  value: string;
}

function Stat({ label, value }: StatProps) {
  return (
    <div className="tss-profile-header__stat">
      <span className="tss-profile-header__stat-value">{value}</span>
      <span className="tss-profile-header__stat-label">{label}</span>
    </div>
  );
}

export const ProfileHeader = forwardRef<HTMLDivElement, ProfileHeaderProps>(function ProfileHeader(
  { profile, className, ...rest },
  ref
) {
  const name = getDisplayName(profile);
  const initials = getInitials(profile);
  const age = calculateAge(profile.date_of_birth);

  const stats: StatProps[] = [];
  if (age !== null) stats.push({ label: 'Age', value: String(age) });
  if (profile.height) stats.push({ label: 'Height', value: profile.height });
  if (profile.weight) stats.push({ label: 'Weight', value: profile.weight });
  if (profile.position) stats.push({ label: 'Position', value: profile.position });
  if (profile.jersey_number) {
    stats.push({ label: 'Jersey', value: `#${profile.jersey_number}` });
  }

  return (
    <div ref={ref} data-tss-ui="" className={cn('tss-profile-header', className)} {...rest}>
      <div className="tss-profile-header__avatar" aria-hidden={!profile.profile_picture_url}>
        {profile.profile_picture_url ? (
          <img
            src={profile.profile_picture_url}
            alt={name}
            className="tss-profile-header__avatar-img"
          />
        ) : (
          <span className="tss-profile-header__avatar-initials">{initials}</span>
        )}
      </div>
      <div className="tss-profile-header__body">
        <h2 className="tss-profile-header__name">{name}</h2>
        {stats.length > 0 && (
          <ul className="tss-profile-header__stats" aria-label="Profile stats">
            {stats.map((s) => (
              <li key={s.label}>
                <Stat label={s.label} value={s.value} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
});
